import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import BrandLogo from "./BrandLogo.jsx";
import { scrollToId, FadeUp } from "./Reveal.jsx";

const LINKS = [
  { id: "about", label: "About" },
  { id: "facilities", label: "Facilities" },
  { id: "gallery", label: "Gallery" },
  { id: "location", label: "Location & Contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 150 : 0);
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "border-b hairline bg-white/85 shadow-[0_8px_30px_rgba(7,58,30,0.06)] backdrop-blur-xl"
            : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-10">
          <button
            className="flex items-center gap-3"
            onClick={() => window.__lenis ? window.__lenis.scrollTo(0, { duration: 1.2 }) : window.scrollTo({ top: 0, behavior: "smooth" })}
            data-testid="navbar-home-logo"
          >
            <BrandLogo size={44} rounded="rounded-xl" fallbackBg="bg-brand shadow-[0_6px_18px_rgba(13,91,49,0.28)]" testid="navbar-logo" />
            <span className="text-left leading-tight">
              <span className="block font-display text-[15px] font-semibold tracking-[0.08em] text-brand">
                ANGUKUMARAN FARMHOUSE
              </span>
              <span lang="ta" className="block font-tamil text-[11px] text-brand-light">
                அங்கு குமரன் பண்ணை இல்லம்
              </span>
            </span>
          </button>

          <nav className="hidden items-center gap-8 lg:flex">
            {LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                className="group relative text-[13px] font-semibold uppercase tracking-[0.14em] text-zinc-700 transition-colors hover:text-brand"
                data-testid={`navbar-link-${l.id}`}
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-brand-light transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
            <button
              onClick={() => go("booking")}
              className="group flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-[13px] font-semibold uppercase tracking-[0.14em] text-white transition-all duration-300 hover:bg-brand-deep hover:shadow-[0_10px_28px_rgba(13,91,49,0.35)]"
              data-testid="navbar-book-now"
            >
              Book Now
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
            </button>
          </nav>

          <button
            className="flex h-11 w-11 items-center justify-center rounded-xl border hairline text-brand lg:hidden"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="navbar-mobile-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      {open && (
        <div className="fixed inset-0 z-40 flex flex-col justify-center bg-brand-dark px-8 lg:hidden">
          <FadeUp>
            <p lang="ta" className="font-tamil text-sm text-brand-light">
              அங்கு குமரன் பண்ணை இல்லம்
            </p>
          </FadeUp>
          <nav className="mt-6 flex flex-col gap-2">
            {[...LINKS, { id: "booking", label: "Book Your Stay" }].map((l, i) => (
              <FadeUp key={l.id} delay={0.06 * i}>
                <button
                  onClick={() => go(l.id)}
                  className="w-full border-b border-white/10 py-4 text-left font-display text-3xl text-white transition-colors hover:text-brand-light"
                  data-testid={`mobile-link-${l.id}`}
                >
                  {l.label}
                </button>
              </FadeUp>
            ))}
          </nav>
          <FadeUp delay={0.3}>
            <p className="mt-10 text-sm text-white/60">+91 99363 56991 · angukumaranfarmhouse@gmail.com</p>
          </FadeUp>
        </div>
      )}
    </>
  );
};

export default Navbar;
